import { useEffect } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import { KIDS } from "../../data/kids";
import SEO from "../../components/SEO";
import ShareBar from "../../components/ShareBar";

export default function KidProfile() {
  const { slug } = useParams();
  const kid = KIDS.find((k) => k.slug === slug);
  useEffect(() => { window.scrollTo(0, 0); }, [slug]);

  if (!kid) return <Navigate to="/meet-the-kids" replace />;

  // Per-profile structured data — ProfilePage with embedded Person
  // (representative archetype based on national foster-care trends).
  const profileSchema = {
    "@context": "https://schema.org",
    "@type": "ProfilePage",
    "mainEntity": {
      "@type": "Person",
      "name": kid.name,
      "alternateName": `Meet ${kid.name}`,
      "description": kid.short || kid.summary,
      "image": kid.hero.startsWith("http") ? kid.hero : `https://fostercareawareness365.com${kid.hero}`,
      "disambiguatingDescription": "Representative foster youth profile based on national U.S. foster care trends — created by Foster Care Awareness 365 to humanize system-level data."
    },
    "headline": `Meet ${kid.name}, Age ${kid.age} — ${kid.tagline}`,
    "url": `https://fostercareawareness365.com/meet-the-kids/${kid.slug}`,
    "isPartOf": {
      "@type": "WebSite",
      "@id": "https://fostercareawareness365.com/#website",
      "name": "Foster Care Awareness 365"
    },
    "about": {
      "@type": "Thing",
      "name": "Foster care advocacy"
    }
  };

  // neighbor kids
  const idx = KIDS.findIndex((k) => k.slug === slug);
  const prev = KIDS[(idx - 1 + KIDS.length) % KIDS.length];
  const next = KIDS[(idx + 1) % KIDS.length];

  return (
    <div data-testid={`kid-profile-${slug}`}>
      <SEO
        path={`/meet-the-kids/${slug}`}
        title={`Meet ${kid.name}, Age ${kid.age} | ${kid.tagline}`}
        description={`${kid.name}'s story is a representative foster youth profile based on national trends. ${kid.tagline}. Discover background, personality, current needs, and how you can show up.`}
        image={kid.hero}
        type="article"
        jsonLd={profileSchema}
      />
      <section className="border-b border-fc-border">
        <div className="container-xl pt-16 pb-10">
          <Link to="/meet-the-kids" className="overline link-ul flex items-center gap-2 mb-8 text-fc-muted" data-testid="kid-back">
            <ArrowLeft size={14} /> All Profiles
          </Link>
          <div className="grid md:grid-cols-12 gap-10">
            <div className="md:col-span-5">
              <div className="aspect-[3/4] overflow-hidden border border-fc-border">
                <img src={kid.hero} alt={kid.name} className="w-full h-full object-cover" />
              </div>
            </div>
            <div className="md:col-span-7">
              <div className="overline text-fc-accent mb-4">Age {kid.age} · Featured Profile</div>
              <h1 className="font-display text-6xl md:text-8xl leading-[0.9]">{kid.name}</h1>
              <p className="mt-4 font-display italic text-2xl md:text-3xl text-fc-accent leading-tight">
                {kid.tagline}
              </p>
              <p className="mt-6 text-fc-muted text-base leading-relaxed">{kid.summary}</p>
              <div className="mt-10 flex flex-wrap gap-3">
                <Link to="/get-involved" className="btn-primary" data-testid="kid-give">Support Kids Like {kid.name}</Link>
                {kid.youtube && (
                  <a href={kid.youtube} target="_blank" rel="noopener noreferrer" className="btn-outline" data-testid="kid-video">
                    Watch on YouTube <ArrowUpRight size={14} />
                  </a>
                )}
              </div>

              <ShareBar
                url={`/meet-the-kids/${kid.slug}`}
                title={`Meet ${kid.name}, age ${kid.age} — "${kid.tagline}" · #Foster365`}
                testIdPrefix={`kid-share-${kid.slug}`}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Experience card */}
      <section className="border-b border-fc-border bg-fc-surface">
        <div className="container-xl py-14">
          <div className="grid md:grid-cols-12 gap-10 items-center">
            <div className="md:col-span-7">
              <img src={kid.experience_card} alt={`${kid.name}'s experience`} className="w-full border border-fc-border" />
            </div>
            <div className="md:col-span-5">
              <div className="overline text-fc-muted mb-4">{kid.name}'s Experience</div>
              <blockquote className="font-display text-2xl md:text-3xl italic leading-tight">
                "{kid.tagline}"
              </blockquote>
            </div>
          </div>
        </div>
      </section>

      {/* YouTube embed */}
      {kid.youtube_id && (
        <section className="border-b border-fc-border">
          <div className="container-xl py-16">
            <div className="overline text-fc-muted mb-4">Meet {kid.name} · Video</div>
            <div className="aspect-video border border-fc-border bg-fc-ink overflow-hidden">
              <iframe
                className="w-full h-full"
                src={`https://www.youtube.com/embed/${kid.youtube_id}`}
                title={`Meet ${kid.name}`}
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          </div>
        </section>
      )}

      {/* Background / Personality */}
      <section className="border-b border-fc-border">
        <div className="container-xl py-16 grid md:grid-cols-2 gap-12">
          <div>
            <div className="overline text-fc-accent mb-4">Background</div>
            <ul className="space-y-3 text-fc-ink/85">
              {kid.background.map((b, i) => (
                <li key={i} className="flex gap-3 border-b border-fc-border pb-3 last:border-0 last:pb-0">
                  <span className="font-mono text-fc-muted text-sm pt-1">{String(i+1).padStart(2,"0")}</span>
                  <span className="leading-relaxed">{b}</span>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <div className="overline text-fc-accent mb-4">Personality & Traits</div>
            <ul className="space-y-3 text-fc-ink/85">
              {kid.personality.map((b, i) => (
                <li key={i} className="flex gap-3 border-b border-fc-border pb-3 last:border-0 last:pb-0">
                  <span className="font-mono text-fc-muted text-sm pt-1">{String(i+1).padStart(2,"0")}</span>
                  <span className="leading-relaxed">{b}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Likes / Dislikes */}
      <section className="border-b border-fc-border bg-fc-surface">
        <div className="container-xl py-16 grid md:grid-cols-2 gap-12">
          <div>
            <div className="overline text-fc-accent mb-4">Likes</div>
            <ul className="space-y-2">
              {kid.likes.map((l, i) => (
                <li key={i} className="flex gap-3 items-start">
                  <span className="text-fc-accent mt-1">+</span>
                  <span className="text-fc-ink/85 leading-relaxed">{l}</span>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <div className="overline text-fc-accent mb-4">Dislikes</div>
            <ul className="space-y-2">
              {kid.dislikes.map((l, i) => (
                <li key={i} className="flex gap-3 items-start">
                  <span className="text-fc-muted mt-1">—</span>
                  <span className="text-fc-ink/85 leading-relaxed">{l}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Current Needs */}
      <section className="border-b border-fc-border">
        <div className="container-xl py-16">
          <div className="overline text-fc-accent mb-4">Current Needs</div>
          <h2 className="font-display text-3xl md:text-5xl mb-10 leading-tight max-w-3xl">
            How a family or community can show up for <em className="text-fc-accent">{kid.name}</em>.
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {kid.needs.map((n, i) => (
              <div key={i} className="border border-fc-border p-6 bg-fc-bg">
                <div className="font-mono text-fc-accent text-sm mb-3">{String(i+1).padStart(2,"0")}</div>
                <p className="text-fc-ink/85 leading-relaxed text-sm">{n}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Bonus video for Malik/Ariana */}
      {kid.bonus_video_id && (
        <section className="border-b border-fc-border bg-fc-surface">
          <div className="container-xl py-16">
            <div className="overline text-fc-muted mb-3">Bonus Story</div>
            <h3 className="font-display text-2xl md:text-3xl mb-6">{kid.bonus_video_title}</h3>
            <div className="aspect-video border border-fc-border bg-fc-ink overflow-hidden max-w-4xl">
              <iframe
                className="w-full h-full"
                src={`https://www.youtube.com/embed/${kid.bonus_video_id}`}
                title={kid.bonus_video_title}
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          </div>
        </section>
      )}

      {/* Prev / Next nav */}
      <section className="border-b border-fc-border">
        <div className="container-xl py-12 grid grid-cols-2 gap-6">
          <Link to={`/meet-the-kids/${prev.slug}`} className="group" data-testid="kid-prev">
            <div className="overline text-fc-muted mb-2">← Previous</div>
            <div className="font-display text-2xl md:text-3xl group-hover:text-fc-accent transition-colors">{prev.name}</div>
          </Link>
          <Link to={`/meet-the-kids/${next.slug}`} className="group text-right" data-testid="kid-next">
            <div className="overline text-fc-muted mb-2">Next →</div>
            <div className="font-display text-2xl md:text-3xl group-hover:text-fc-accent transition-colors">{next.name}</div>
          </Link>
        </div>
      </section>
    </div>
  );
}
